import React, { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Divider,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
} from "@mui/icons-material";
import useFaqData from "../hooks/useFaqData";
import EditForm from "./EditForm";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

function SortableItem({ faq, index, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: faq.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1,
        border: "1px solid #e0e0e0",
        backgroundColor: isDragging ? "#f5f5f5" : "white",
        transition: "background-color 0.2s ease",
        "&:hover": {
          boxShadow: 2,
        },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" gap={2}>
          <Box {...attributes} {...listeners}>
            <DragIcon
              sx={{
                color: "#666",
                cursor: "grab",
                "&:active": { cursor: "grabbing" },
              }}
            />
          </Box>

          <Box
            sx={{
              backgroundColor: "#AC1A2F",
              color: "white",
              borderRadius: "50%",
              width: 24,
              height: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.75rem",
              fontWeight: "bold",
            }}
          >
            {index + 1}
          </Box>

          <Box flex={1}>
            <Typography variant="h6" component="div" gutterBottom>
              {faq.title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {faq.content}
            </Typography>
          </Box>

          <Box display="flex" gap={1}>
            <IconButton
              onClick={() => onEdit(faq.id)}
              color="primary"
              size="small"
            >
              <EditIcon />
            </IconButton>
            <IconButton
              onClick={() => onDelete(faq.id)}
              color="error"
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

const CrudTable = () => {
  const { data, loading, error, updateData, deleteData, updateFaqOrder } =
    useFaqData();
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [items, setItems] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sort FAQs by order for display and update local state
  useEffect(() => {
    const sortedData = [...data].sort(
      (a, b) => (a.faq_order || 0) - (b.faq_order || 0)
    );
    setItems(sortedData);
  }, [data]);

  const handleEditClick = (faqId) => {
    const faq = data.find((item) => item.id === faqId);
    setSelectedFaq(faq);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (faqId) => {
    const faq = data.find((item) => item.id === faqId);
    setSelectedFaq(faq);
    setDeleteDialogOpen(true);
  };

  const handleEditFormClose = () => {
    setEditDialogOpen(false);
    setSelectedFaq(null);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedFaq(null);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleUpdate = async (updatedData) => {
    try {
      await updateData(updatedData);
      setSnackbarMessage("FAQ has been updated");
      setSnackbarOpen(true);
      handleEditFormClose();
    } catch (error) {
      console.error("Error updating faq:", error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteData(selectedFaq.id);
      setSnackbarMessage("FAQ has been deleted");
      setSnackbarOpen(true);
      handleDeleteDialogClose();
    } catch (error) {
      console.error("Error deleting faq:", error);
    }
  };

  const handleOnDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const newItems = arrayMove(items, oldIndex, newIndex);

      // Update local state immediately for better UX
      setItems(newItems);

      try {
        await updateFaqOrder(newItems);
        setSnackbarMessage("FAQ order has been updated");
        setSnackbarOpen(true);
      } catch (error) {
        console.error("Error updating order:", error);
        // Revert local state on error
        setItems(items);
        setSnackbarMessage("Error updating FAQ order");
        setSnackbarOpen(true);
      }
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          FAQs (Drag to reorder)
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleOnDragEnd}
        >
          <SortableContext
            items={items.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            {items.map((faq, index) => (
              <SortableItem
                key={faq.id}
                faq={faq}
                index={index}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
              />
            ))}
          </SortableContext>
        </DndContext>

        {items.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              color: "text.secondary",
            }}
          >
            <Typography variant="body1">
              No FAQs found. Create your first FAQ using the form on the left.
            </Typography>
          </Box>
        )}
      </Paper>
      <Dialog open={editDialogOpen} onClose={handleEditFormClose}>
        <ModalDialogTitle onClose={handleEditFormClose}>
          Edit Faq
        </ModalDialogTitle>
        <DialogContent>
          {selectedFaq && (
            <EditForm initialData={selectedFaq} onUpdate={handleUpdate} />
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this faq?"
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      />
    </>
  );
};

export default CrudTable;
